// src/components/ChatNotificationToggle.js
import { useState } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { registerChatPushNotifications } from "../services/chatPushRegistration";

const ICON_SIZE = 14;

/**
 * ChatNotificationToggle — a small opt-in control for chat push notifications.
 *
 * Reflects the browser's Notification permission and lets the user turn chat
 * alerts on with one tap (which is when the permission prompt appears — never
 * auto-prompted). States:
 *   - default  → tappable "Enable notifications"
 *   - granted  → subtle "Notifications on"
 *   - denied   → "Notifications blocked" (can only be re-enabled in settings)
 *   - iOS in a browser tab → "Add to Home Screen for alerts" (web push on iOS
 *     needs the PWA installed, iOS 16.4+)
 *   - genuinely unsupported → renders nothing.
 */

const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.platform || "") ||
  (/Mac/.test(navigator.userAgent || "") && "ontouchend" in document);

const isStandalone = () =>
  (typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches) ||
  window.navigator.standalone === true;

const basePill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 999,
  padding: "4px 12px",
  border: "none",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
};

export default function ChatNotificationToggle({ onForeground, style = {} }) {
  const hasNotification = typeof window !== "undefined" && "Notification" in window;
  const [perm, setPerm] = useState(hasNotification ? Notification.permission : "unsupported");
  const [busy, setBusy] = useState(false);

  // iOS Safari can't do web push in a normal tab — nudge to install.
  if (isIOS() && !isStandalone()) {
    return (
      <div
        title="On iPhone, add Muuzbox to your Home Screen to receive chat notifications."
        style={{ ...basePill, background: "rgba(245,158,11,0.15)", color: "#fbbf24", cursor: "default", ...style }}
      >
        <Bell size={ICON_SIZE} /> Add to Home Screen for alerts
      </div>
    );
  }

  // Truly unsupported (no Notification / Service Worker / Push APIs) → hide.
  const supported =
    hasNotification && "serviceWorker" in navigator && "PushManager" in window;
  if (!supported) return null;

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await registerChatPushNotifications({ onForeground });
    } catch (_) {
      /* non-fatal */
    } finally {
      setPerm("Notification" in window ? Notification.permission : perm);
      setBusy(false);
    }
  };

  if (perm === "granted") {
    return (
      <div
        title="Chat notifications are on"
        style={{ ...basePill, background: "rgba(34,197,94,0.15)", color: "#86efac", cursor: "default", ...style }}
      >
        <BellRing size={ICON_SIZE} /> Notifications on
      </div>
    );
  }

  if (perm === "denied") {
    return (
      <div
        title="Notifications are blocked. Re-enable them in your browser/site settings."
        style={{ ...basePill, background: "rgba(148,163,184,0.15)", color: "#cbd5e1", cursor: "default", ...style }}
      >
        <BellOff size={ICON_SIZE} /> Notifications blocked
      </div>
    );
  }

  // default → tappable opt-in
  return (
    <button
      type="button"
      onClick={handleEnable}
      disabled={busy}
      style={{
        ...basePill,
        background: "rgba(59,130,246,0.18)",
        color: "#93c5fd",
        border: "1px solid rgba(59,130,246,0.35)",
        cursor: busy ? "wait" : "pointer",
        ...style,
      }}
    >
      <Bell size={ICON_SIZE} /> {busy ? "Enabling…" : "Enable notifications"}
    </button>
  );
}
